import { useEffect, useState } from 'react'
import { useAppStore } from '@core/store'
import { viewerApi } from '@viewer/lib/viewerApi'
import { formatValue, getCategory } from '@viewer/lib/ifcItemData'
import { useNomesDosModelos } from '@viewer/lib/useNomeDoModelo'

// Bottom-left status bar showing what the cursor is hovering, plus a
// floating tooltip that follows the cursor with a 14px offset. Both
// read from store.hover and resolve metadata via getItemsData (cached
// per loaded model instance and localId, so repeated hovers don't
// re-fetch and a re-opened file never shows the previous version).

interface Meta {
  category: string
  name: string
  tag: string
}

/** Meta por modelo CARREGADO (a instância do Fragments), não pelo
 *  modelId: o modelId é o nome do ficheiro, e reabrir uma versão nova
 *  do mesmo ficheiro dava a classe e o nome da versão anterior para o
 *  mesmo localId. Com a instância como chave, um modelo novo começa com
 *  a cache vazia e a do antigo vai com ele. */
const cache = new WeakMap<object, Map<number, Meta>>()
const inflight = new Set<string>()

function key(modelId: string, localId: number) {
  return `${modelId}:${localId}`
}

function cached(modelId: string, localId: number): Meta | null {
  const model = viewerApi.getModel(modelId)
  if (!model) return null
  return cache.get(model)?.get(localId) ?? null
}

async function fetchMeta(
  modelId: string,
  localId: number,
  onResolved: (m: Meta) => void,
) {
  const hit = cached(modelId, localId)
  if (hit) {
    onResolved(hit)
    return
  }
  const k = key(modelId, localId)
  if (inflight.has(k)) return
  const model = viewerApi.getModel(modelId)
  if (!model) return
  inflight.add(k)
  try {
    const items = await model.getItemsData([localId], {
      attributesDefault: true,
      relationsDefault: { attributes: false, relations: false },
    })
    const item = items[0]
    if (!item || typeof item !== 'object') return
    const o = item as Record<string, unknown>
    const m: Meta = {
      category: getCategory(o),
      name: formatValue(o.Name),
      tag: formatValue(o.Tag),
    }
    let porModelo = cache.get(model)
    if (!porModelo) {
      porModelo = new Map()
      cache.set(model, porModelo)
    }
    porModelo.set(localId, m)
    onResolved(m)
  } finally {
    inflight.delete(k)
  }
}

function HoverOverlay() {
  const hover = useAppStore((s) => s.hover)
  const cursor = useAppStore((s) => s.hoverCursor)
  const marqueeMode = useAppStore((s) => s.marqueeMode)
  const selectedCount = useAppStore((s) => s.selectedTargets.length)
  // Com vários modelos federados, a etiqueta diz de que modelo é o
  // elemento (na barra, o nome curto; junto ao cursor, só a sigla). Com
  // um só modelo não há ambiguidade e não se repete.
  const { nomeDe, varios } = useNomesDosModelos()
  const modelo = hover && varios ? nomeDe(hover.modelId) : null
  const hoverKey = hover ? key(hover.modelId, hover.localId) : null
  const [meta, setMeta] = useState<Meta | null>(
    () => (hover ? cached(hover.modelId, hover.localId) : null),
  )

  // Ao mudar de elemento sob o cursor, adotamos o valor durante o render
  // (com guarda de referência sobre a chave): cache se existir, senão
  // null. Antes isto era um efeito com setState síncrono, o que obrigava
  // a um render extra com dados do elemento anterior.
  const [prevHoverKey, setPrevHoverKey] = useState(hoverKey)
  if (prevHoverKey !== hoverKey) {
    setPrevHoverKey(hoverKey)
    setMeta(hover ? cached(hover.modelId, hover.localId) : null)
  }

  // O efeito trata só do que é assíncrono: procurar a meta em falta.
  useEffect(() => {
    if (!hover) return
    if (cached(hover.modelId, hover.localId)) return
    let cancelled = false
    void fetchMeta(hover.modelId, hover.localId, (m) => {
      if (!cancelled) setMeta(m)
    })
    return () => {
      cancelled = true
    }
  }, [hover])

  // Mode/status banner shown bottom-left even without hover info, so
  // the user always knows the active mode and how to leave it.
  const statusBanner = (() => {
    if (marqueeMode) {
      return (
        <span className="font-mono text-[10px] uppercase tracking-wide text-[var(--color-accent)]">
          Modo Janela ativo · Esc para sair
        </span>
      )
    }
    if (!hover && selectedCount > 0) {
      return (
        <span className="text-[10px] text-[var(--color-text-muted)]">
          {selectedCount} elemento(s) selecionado(s) · Esc para limpar
        </span>
      )
    }
    return null
  })()

  const cat = meta?.category || '…'
  const name = meta && meta.name !== '-' ? meta.name : ''
  const tag = meta && meta.tag !== '-' ? meta.tag : ''

  if (!hover && !statusBanner) return null

  return (
    <>
      <div className="pointer-events-none absolute bottom-3 left-3 z-10 flex max-w-[480px] items-center gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)]/95 px-2.5 py-1 text-xs shadow">
        {hover ? (
          <>
            <span className="font-mono text-[10px] uppercase tracking-wide text-[var(--color-accent)]">
              {cat}
            </span>
            {name && (
              <span className="truncate text-[var(--color-text)]">{name}</span>
            )}
            {tag && (
              <span className="font-mono text-[10px] text-[var(--color-text-muted)]">
                Tag {tag}
              </span>
            )}
            <span className="font-mono text-[10px] text-[var(--color-text-dim)]">
              #{hover.localId}
            </span>
            {modelo && (
              <span className="truncate font-mono text-[10px] text-[var(--color-text-muted)]" data-modelo-na-etiqueta>
                {modelo.curto}
              </span>
            )}
            <span className="ml-2 text-[10px] text-[var(--color-text-dim)]">
              Tab/⇧Tab para próximo
            </span>
          </>
        ) : (
          statusBanner
        )}
      </div>
      {hover && cursor && !marqueeMode && (
        <div
          className="pointer-events-none absolute z-30 whitespace-nowrap rounded border border-[var(--color-border)] bg-[var(--color-surface)]/95 px-2 py-0.5 text-[10px] shadow"
          style={{
            left: cursor.x + 14,
            top: cursor.y + 14,
          }}
        >
          <span className="font-mono uppercase text-[var(--color-accent)]">
            {cat}
          </span>
          {name && <span className="ml-1.5 text-[var(--color-text)]">{name}</span>}
          {modelo && (
            <span className="ml-1.5 font-mono text-[var(--color-text-muted)]">{modelo.minimo}</span>
          )}
        </div>
      )}
    </>
  )
}

export default HoverOverlay
